import { Grid, IconButton } from '@material-ui/core'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import * as React from 'react'

import Box from '../base/Box'
import Typography from '../base/Typography'
import Button from '../button/Button'
import PackageJobVisitLink, {
  PackageJobVisitProps
} from '../button/PackageJobVisitLink'
import Icon from '../icons/Icons'
import TextArea from '../input/TextareaAutosize'
import Tag from '../tags/Tags'

export interface PackageJobVisitDetail {
  name: string
  addressCode: string
  address: string
  phone: string
  postalCode: string
  contact: string
  note?: string
}

export type PackageJobVisitDetailProps = {
  detailsValues: PackageJobVisitDetail
  onClose(event: React.MouseEvent<HTMLElement>): void
} & PackageJobVisitProps

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    labelColor: {
      color: theme.palette.grey[800]
    },
    address: {
      marginRight: theme.spacing(5)
    },
    phone: {
      marginRight: theme.spacing(6)
    },
    postalCode: {
      marginRight: theme.spacing(2.5)
    },
    contactPerson: {
      marginRight: theme.spacing(0.7)
    },
    textArea: {
      ...theme.typography.caption,
      marginRight: theme.spacing(2),
      width: 200
    },
    closeIcon: {
      position: 'absolute',
      right: 0
    },
    arrow: {
      position: 'absolute',
      right: '-12px',
      top: '47%',
      color: theme.palette.common.white
    },
    linkStyle: {
      ...theme.typography.caption,
      color: theme.palette.info.main,
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  })
)

const PackageJobVisitDetail: React.FC<PackageJobVisitDetailProps> = (props) => {
  const classes = useStyles()
  const {
    detailsValues,
    onClose,
    packageJobVisit,
    packageJobVisit: { visitId, onClickVisit }
  } = props
  return (
    <Box>
      <Box className={classes.arrow}>
        <Icon iconName="ArrowFillRightIcon" size="small" />
      </Box>
      <IconButton
        onClick={(event: React.MouseEvent<HTMLElement>) => onClose(event)}
        className={classes.closeIcon}
      >
        <Icon iconName="CloseIcon" size="small" />
      </IconButton>
      <Box padding={2}>
        <Box borderLeft="3px solid #37B24D" paddingLeft={2} marginBottom={2}>
          <Box marginBottom={2}>
            <Typography
              bodyVariants="smBold"
              children={detailsValues ? detailsValues.name : 'No Name'}
            />
            <Grid container direction="row">
              <Box
                marginRight={3}
                display="flex"
                alignItems="center"
                gridGap={8}
              >
                <PackageJobVisitLink {...packageJobVisit} />
              </Box>
              <Tag
                customVariant="secondary"
                sizes="small"
                label={detailsValues ? detailsValues.addressCode : 'Empty'}
              />
            </Grid>
          </Box>
          <Grid
            container
            direction="column"
            justify="flex-start"
            alignItems="baseline"
          >
            <Box>
              <Typography
                className={`${classes.labelColor} ${classes.address}`}
                variant="caption"
                children="Address:"
              />
              <Typography
                variant="caption"
                children={detailsValues ? detailsValues.address : 'No Address'}
              />
            </Box>
            <Box>
              <Typography
                className={`${classes.labelColor} ${classes.phone}`}
                variant="caption"
                children="Phone:"
              />
              <Typography
                variant="caption"
                children={detailsValues ? detailsValues.phone : '-'}
              />
            </Box>
            <Box>
              <Typography
                className={`${classes.labelColor} ${classes.postalCode}`}
                variant="caption"
                children="Postal Code:"
              />
              <Typography
                variant="caption"
                children={detailsValues ? detailsValues.postalCode : 0}
              />
            </Box>
            <Box>
              <Typography
                className={`${classes.labelColor} ${classes.contactPerson}`}
                variant="caption"
                children="Contact Person:"
              />
              <Typography
                variant="caption"
                children={detailsValues ? detailsValues.contact : 'No Contact'}
              />
            </Box>
          </Grid>
        </Box>
        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="center"
        >
          <TextArea
            rows={4}
            readOnly
            className={classes.textArea}
            value={detailsValues ? detailsValues.note : 'Note Empty'}
          />
          {visitId && onClickVisit && (
            <Button
              onClick={(e) => onClickVisit(e, visitId)}
              customSize="xs"
              variants="ghost"
              children="View Detail"
            />
          )}
        </Grid>
      </Box>
    </Box>
  )
}

export default PackageJobVisitDetail
